/**
 * Icon helpers — thin convenience layer over `icons.css` (CSS-mask Lucide system).
 * The icon catalog lives in `tools/gen_icons.mjs`; regenerate with:
 *   node tools/gen_icons.mjs > src/cpq_agent/webchat_ui/icons.css
 *
 * Usage:
 *   import { iconHtml, icon, kbd } from "./icons.js";
 *   iconHtml("check")                       // '<i class="ic ic-check" aria-hidden="true"></i>'
 *   iconHtml("undo", { size: "sm" })        // '<i class="ic ic-sm ic-undo" aria-hidden="true"></i>'
 *   iconHtml("trash-2", { title: "Delete" })// labelled, sets aria-label + title
 *   icon("check")                            // DOM <i> element for el()-builder callers
 *   kbd("⌘K")                                // '<kbd class="kbd">⌘K</kbd>'
 *
 * Unknown names: warn once in the console and return empty string / empty <i>.
 */

const KNOWN = new Set([
  // Originals (gen_icons.mjs first block)
  "loader", "check", "circle-pause", "plus", "square", "x",
  "undo", "redo", "play", "box", "activity", "git-branch",
  "file", "folder", "file-code", "refresh-cw", "search", "settings",
  "chevron-right", "chevron-down",
  "chevron-up", "chevron-left",
  "arrow-up", "arrow-down", "arrow-left", "arrow-right",
  "arrow-up-from-line", "arrow-down-to-line",
  "at-sign", "git-pull-request", "git-commit",
  "eye", "eye-off", "upload", "download", "save",
  "edit-3", "trash-2",
  "alert-triangle", "alert-circle", "info",
  "pin", "pin-off",
  "more-horizontal", "more-vertical", "columns-2",
  "terminal", "terminal-square", "code", "code-2",
  "database", "layers", "history",
  "send", "paperclip", "link", "external-link",
  "monitor", "cpu", "sparkles", "wand-2",
  "list", "file-text", "file-json", "file-x",
  "x-circle", "check-circle", "circle",
  "sliders-horizontal", "filter", "flag", "zap",
  "user", "archive", "cloud", "rewind",
  // Legacy short-form aliases (preserved for rewind UI consumers)
  "edit", "trash", "chevron", "alert",
  // v2 Code workbench -- detail-head Copy button
  "copy",
  // Workbench polish -- table-2/diff, not yet wired into markup
  // (see tools/gen_icons.mjs's own comment for where they'd be used).
  "table-2", "diff",
  // Environment Ladder -- PROD station gate (locked/unlocked).
  "lock", "unlock",
]);

const _warned = new Set();
function warnUnknown(name) {
  if (_warned.has(name)) return;
  _warned.add(name);
  console.warn(`[icons] unknown icon name: "${name}". Add it to tools/gen_icons.mjs.`);
}

function sizeClass(size) {
  return size === "sm" ? "ic-sm" : size === "lg" ? "ic-lg" : "";
}

/**
 * Return an HTML string for the icon. Use for template-literal callers.
 * opts: { size?: "sm"|"lg", class?: string, title?: string }
 */
export function iconHtml(name, opts = {}) {
  if (!KNOWN.has(name)) {
    warnUnknown(name);
    return "";
  }
  const classes = ["ic", `ic-${name}`];
  const s = sizeClass(opts.size);
  if (s) classes.push(s);
  if (opts.class) classes.push(opts.class);
  const a11y = opts.title
    ? ` aria-label="${escAttr(opts.title)}" title="${escAttr(opts.title)}"`
    : ` aria-hidden="true"`;
  return `<i class="${classes.join(" ")}"${a11y}></i>`;
}

/**
 * Return a DOM <i> element. Use for el()-builder callers.
 * opts: same shape as iconHtml.
 */
export function icon(name, opts = {}) {
  const el = document.createElement("i");
  if (!KNOWN.has(name)) {
    warnUnknown(name);
    return el;
  }
  const classes = ["ic", `ic-${name}`];
  const s = sizeClass(opts.size);
  if (s) classes.push(s);
  if (opts.class) classes.push(opts.class);
  el.className = classes.join(" ");
  if (opts.title) {
    el.title = opts.title;
    el.setAttribute("aria-label", opts.title);
  } else {
    el.setAttribute("aria-hidden", "true");
  }
  return el;
}

/**
 * Wrap a keyboard glyph or label in a styled <kbd> element.
 * Lucide has no key-cap icons; symbols like ⌘ ⇧ ⏎ ⌥ ⎋ ↑ ↓ ⌫ stay as text.
 */
export function kbd(label) {
  return `<kbd class="kbd">${escText(label)}</kbd>`;
}

function escAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}
function escText(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
